import moment from "moment";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { StringParam, useQueryParam } from "use-query-params";
import { Whatsapp } from "../../../../assets/media/icons";
import Info from "../../../../assets/media/svgs/Info";
import { CurrencyContext } from "../../../../contexts/currency";
import { ToastContext } from "../../../../contexts/toast";
import {
  AvailabilityInfo,
  Booking,
  BookingRoom,
} from "../../../../definitions/booking";
import { PropertyRoom } from "../../../../definitions/property";
import { useUnauthenticatedApi } from "../../../../hooks/useApi";
import useBookingService from "../../../../hooks/useBookingService";
import { formatDateForServer } from "../../../../services/booking/utils";
import { smoothScrollTo } from "../../../../utils/animate";
import { processCancellationPolicies } from "../../../../utils/ProcessCancellationPolicies";
import { getTrackingInfo, gtag } from "../../../../utils/track";
import { trackZostelEvent } from "../../../../utils/tracking";
import Container from "../../../structure/Container";
import Flex from "../../../structure/Flex";
import { Accordion } from "../../Accordion";
import Amenities from "../../Amenities";
import Button from "../../Button";
import { CancellationPolicyGrid } from "../../CancellationPolicyGrid";
import CollapsibleContent from "../../CollapsibleContent";
import CollapsibleText from "../../CollapsibleText";
import Icon from "../../Icon";
import ZMap from "../../ZMap";
import DateSelector from "../DateSelector";
import RoomsList from "../RoomsListLandingPage/RoomsList";
import BookingSummary from "./components/BookingSummary";
import { BottomSheet } from "react-spring-bottom-sheet";

interface PropertyBookerProps {
  propertyCode: string;
  initialRoomsInfo: Array<PropertyRoom>;
  bookableMonths: number;
  minNights?: number;
  bookableOccupancy: number;
  bookingDelay: number;
  hideDate?: boolean;
  minBookingDate?: string;
  defaultNights?: number;
  setGalleryVisible?: any;
  setGalleryData?: any;
  propertyInfo?: any;
  moreProperties?: any;
  scrollToOtherStays?: () => void;
  policyRef?: any;
  selectedRoomId?: number | null;
  guestCount?: number;
}

const PropertyBooker: React.FC<PropertyBookerProps> = ({
  initialRoomsInfo,
  propertyCode,
  bookableMonths,
  bookableOccupancy,
  minBookingDate,
  bookingDelay,
  minNights = 1,
  defaultNights = 1,
  hideDate,
  setGalleryVisible,
  setGalleryData,
  propertyInfo,
  moreProperties,
  scrollToOtherStays,
  selectedRoomId,
  guestCount,
}) => {
  const location = useLocation();
  const operatorApi = useUnauthenticatedApi("STAY_OPERATORS");

  const howToReachRef = useRef(null);
  const generalPolicyRef = useRef(null);
  const cancellationPolicyRef = useRef(null);
  const [cancelInfo, setCancelInfo] = useState<any>();

  const bookingService = useBookingService();
  const { addToast } = useContext(ToastContext);
  const [showModal, setShowModal] = useState(false);
  const { currency, setCurrency, allCurrencies, isDataLoading } =
    useContext(CurrencyContext);
  const [isCurrencyDropdownVisible, setIsCurrencyDropdownVisible] =
    useState<boolean>(false);
  const bookingElement = useRef<HTMLElement>(null);

  const [isHowToReachExpanded, setIsHowToReachExpanded] = useState(true);
  const [isGeneralPolicyExpanded, setIsGeneralPolicyExpanded] = useState(false);
  const [isCancellationPolicyExpanded, setIsCancellationPolicyExpanded] =
    useState(false);

  const [
    urlStartDate = moment().add(bookingDelay, "days").format("YYYY-MM-DD"),
    setUrlStartDate,
  ] = useQueryParam("checkin", StringParam);
  const [
    urlEndDate = moment()
      .add(bookingDelay, "days")
      .add(Math.max(defaultNights, minNights), "day")
      .format("YYYY-MM-DD"),
    setUrlEndDate,
  ] = useQueryParam("checkout", StringParam);

  const [bookingDetails, setBookingDetails] = useState<Booking>({
    property_code: propertyCode,
    checkin: moment().add(bookingDelay, "days").format("YYYY-MM-DD"),
    checkout: moment()
      .add(bookingDelay, "days")
      .add(Math.max(defaultNights, minNights), "days")
      .format("YYYY-MM-DD"),
    rooms: [],
    track: getTrackingInfo(),
  });
  const [rooms, setRooms] = useState<Array<PropertyRoom>>([]);
  const [loadingAvailability, setLoadingAvailability] =
    useState<boolean>(false);

  const [localGuestCount, setLocalGuestCount] = useState(guestCount || 2);

  const [isGroupPolicyExpanded, setIsGroupPolicyExpanded] = useState(false);

  const isMobile = useMemo(() => {
    return window.innerWidth < 640;
  }, []);

  useEffect(() => {
    if (operatorApi) {
      operatorApi
        .get(`/${propertyCode}/cancellation-policy/`)
        .then((res) => {
          setCancelInfo(
            processCancellationPolicies(res.data.cancellation_policy)
          );
        })
        .catch(console.log);
    }
  }, [operatorApi, propertyCode]);

  useEffect(() => {
    // if (
    //   checkInDate.diff(startDate, "days") >= 0 &&
    //   checkOutDate.diff(startDate.add(bookableMonths, "months"), "days") <= 0
    // ) {
    const localStartDate = sessionStorage.getItem("eZ_chkin");
    const localEndDate = sessionStorage.getItem("eZ_chkout");

    if (localStartDate && localEndDate) {
      const checkInDate = moment(localStartDate, "DD-MM-YYYY");
      const checkOutDate = moment(localEndDate, "DD-MM-YYYY");
      if (
        checkInDate.isValid() &&
        checkOutDate.isValid() &&
        checkOutDate > checkInDate &&
        checkInDate >= moment().startOf("day")
      ) {
        setUrlStartDate(checkInDate.format("YYYY-MM-DD"));
        setUrlEndDate(checkOutDate.format("YYYY-MM-DD"));
        sessionStorage.removeItem("eZ_chkin");
        sessionStorage.removeItem("eZ_chkout");
        scrollToBooking();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const hash = window.location.hash as
      | "#how-to-reach"
      | "#general-policy"
      | "#cancellation-policy";
    const refs = {
      "#how-to-reach": {
        setExpanded: setIsHowToReachExpanded,
        ref: howToReachRef,
      },
      "#general-policy": {
        setExpanded: setIsGeneralPolicyExpanded,
        ref: generalPolicyRef,
      },
      "#cancellation-policy": {
        setExpanded: setIsCancellationPolicyExpanded,
        ref: cancellationPolicyRef,
      },
    };

    if (hash in refs) {
      const { setExpanded, ref } = refs[hash];
      setTimeout(() => {
        setExpanded(true);
        if (ref.current) smoothScrollTo(ref.current, 1000);
      }, 1000);
    }
  }, []);

  useEffect(() => {
    setBookingDetails((bookingDetails) => {
      bookingDetails.property_code = propertyCode;
      bookingDetails.rooms = [];
      return { ...bookingDetails };
    });
  }, [propertyCode]);

  useEffect(() => {
    if (urlStartDate) {
      const _minBookingDate: moment.Moment =
        moment(minBookingDate).startOf("day");
      let minStartDate: moment.Moment = moment()
        .add(bookingDelay, "days")
        .startOf("day");
      if (_minBookingDate.isAfter(minStartDate)) {
        minStartDate = _minBookingDate;
      }
      if (moment(urlStartDate).startOf("day").isBefore(minStartDate)) {
        addToast(
          `Provided check in date is not bookable, changed to ${minStartDate.format(
            "LL"
          )}!`
        );
        setUrlStartDate(minStartDate.format("YYYY-MM-DD"));
      } else {
        setBookingDetails((bookingDetails) => {
          bookingDetails.checkin = formatDateForServer(moment(urlStartDate));
          return { ...bookingDetails };
        });

        if (urlEndDate) {
          const minEndDate: moment.Moment = moment(urlStartDate)
            .clone()
            .add(minNights, "days")
            .startOf("day");
          if (moment(urlEndDate).startOf("day").isBefore(minEndDate)) {
            setUrlEndDate(minEndDate.format("YYYY-MM-DD"));
          } else {
            setBookingDetails((bookingDetails) => {
              bookingDetails.checkout = formatDateForServer(moment(urlEndDate));
              return { ...bookingDetails };
            });
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingDelay, minNights, urlEndDate, urlStartDate, minBookingDate]);

  useEffect(() => {
    if (location.search && rooms.length) {
      scrollToBooking();
      const params = new URLSearchParams(location.search);
      let roomsString = params.get("rooms");
      const source = params.get("source");

      if (roomsString) {
        if (source === "google") {
          const roomSlit = roomsString.split("-")[1]?.split("@");
          const roomId = roomSlit?.[0];
          const guests = roomSlit?.[1];
          const room = rooms.find((r) => String(r.id) === roomId);
          if (room?.occupancy && guests) {
            const unit = Math.ceil(Number(guests) / room.occupancy);
            roomsString = `${roomId}@${unit}`;
          }
        }
        const roomsInfo = bookingService.decodeRooms(roomsString);
        if (roomsInfo) {
          setBookingDetails((bookingDetails) => {
            bookingDetails.rooms = roomsInfo;
            return { ...bookingDetails };
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, rooms]);
  const isMoveIn = useMemo(() => {
    const queryParams = new URLSearchParams(location.search);
    return queryParams.get("movein") && queryParams.get("movein") === "true";
  }, [location.search]);

  useEffect(() => {
    let mounted = true;
    if (bookingDetails) {
      const checkIn = moment(bookingDetails.checkin).startOf("day");
      const checkOut = moment(bookingDetails.checkout).startOf("day");

      const isValidCheckin = checkIn.isSameOrAfter(
        moment().add(bookingDelay, "days").startOf("day")
      );
      const isValidCheckout = checkOut.isSameOrAfter(
        checkIn.clone().add(minNights, "days").startOf("day")
      );

      if (isValidCheckin && isValidCheckout) {
        setLoadingAvailability(true);
        bookingService
          .getAvailability({
            propertyCode: bookingDetails.property_code,
            checkInDate: checkIn,
            checkOutDate: checkOut,
          })
          .then((res) => {
            const availabilityInfo: AvailabilityInfo = res?.data;
            if (mounted) {
              setRooms(() => {
                const _rooms =
                  bookingService.createOfferedRoomsFromAvailability(
                    initialRoomsInfo,
                    availabilityInfo
                  );
                const moveinRooms = _rooms.filter((r) => r.slab_active);
                return isMoveIn && moveinRooms.length > 0
                  ? moveinRooms
                  : _rooms;
              });
              setLoadingAvailability(false);
            }
          })
          .catch((e) => {
            console.log(e);
          });
      }
    }
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bookingDetails.property_code,
    bookingDetails.checkin,
    bookingDetails.checkout,
    isMoveIn,
    bookingDelay,
    minNights,
  ]);

  const totalOccupancy: number = useMemo(
    () =>
      bookingDetails.rooms.reduce((sum: number, room) => {
        const _room = rooms.find((r) => r.id === room.id);
        if (_room && _room.occupancy) {
          return sum + _room.occupancy * room.count;
        } else {
          return sum + 0;
        }
      }, 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bookingDetails]
  );

  const getRoomCountSetter = useCallback(
    (roomRef: string) => {
      return function (count: number) {
        const roomOccupancy = rooms.find((r) => r._ref === roomRef)?.occupancy;
        if (roomOccupancy != null && bookableOccupancy != null) {
          setBookingDetails((_bookingDetails) => {
            const room = _bookingDetails?.rooms.find(
              (_room) => _room._ref === roomRef
            );
            const offerSplit = roomRef.split("*");
            const roomId = +offerSplit[0].split("-")[0];
            const addonId = offerSplit[0].split("-")[1]
              ? +offerSplit[0].split("-")[1]
              : null;
            const offerId = offerSplit[1] ? +offerSplit[1] : null;
            if (count) {
              if (room) {
                const maxUnits =
                  rooms.find((r) => r.id === room.id)?.availability?.maxUnits ||
                  0;
                const otherSimilarRooms = _bookingDetails?.rooms.filter(
                  (_room) => _room.id === room.id && _room._ref !== roomRef
                );
                let similarRoomsBookedCount = 0;
                otherSimilarRooms.forEach((r) => {
                  similarRoomsBookedCount += r.count;
                });
                if (room.count < count) {
                  if (
                    roomOccupancy + totalOccupancy <= bookableOccupancy &&
                    count + similarRoomsBookedCount <= maxUnits
                  ) {
                    if (addonId) {
                      const addon = room.addons?.find((a) => a.id === addonId);
                      if (addon) {
                        addon.count = count;
                      }
                    }
                    room.count = count;
                  } else {
                    setShowModal(true);
                  }
                } else {
                  if (addonId) {
                    const addon = room.addons?.find((a) => a.id === addonId);
                    if (addon) {
                      addon.count = count;
                    }
                  }
                  room.count = count;
                  setShowModal(false);
                }
              } else {
                if (roomOccupancy + totalOccupancy <= bookableOccupancy) {
                  let newRoom: BookingRoom = {
                    id: roomId,
                    count,
                    _ref: roomRef,
                  };
                  if (addonId) {
                    newRoom.addons = [{ id: addonId, count: newRoom.count }];
                  }
                  if (offerId) {
                    newRoom.offer_id = offerId;
                  }
                  _bookingDetails.rooms.push(newRoom);
                } else {
                  setShowModal(true);
                }
              }
            } else {
              _bookingDetails.rooms = _bookingDetails.rooms.filter(
                (room) => room._ref !== roomRef
              );
            }

            return { ..._bookingDetails };
          });
        }
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bookableOccupancy, rooms, totalOccupancy]
  );

  const roomCounts = useMemo(() => {
    const counts: { [key: string]: number } = {};
    rooms.forEach((room) => {
      const count =
        bookingDetails?.rooms.find((_room) => _room._ref === room._ref)
          ?.count || 0;
      counts[room._ref] = count;
    });
    return counts;
  }, [bookingDetails, rooms]);

  const scrollToBooking = () => {
    smoothScrollTo(bookingElement.current, 1000);
  };

  useEffect(() => {
    setIsCurrencyDropdownVisible(false);
  }, [currency]);

  const handleCurrencyDropDownClick = () => {
    gtag("event", "CurrencyDropDown_Click");
    trackZostelEvent("CurrencyDropDown_Click");
    setIsCurrencyDropdownVisible(true);
  };

  const handleGuestCountChange = useCallback(
    (increment: boolean) => {
      const newCount = increment ? localGuestCount + 1 : localGuestCount - 1;
      if (newCount >= 1) {
        setLocalGuestCount(newCount);
      }
    },
    [localGuestCount]
  );

  const handleShareGroupBookingDetail = useCallback(() => {
    window.open(
      `https://zostel.typeform.com/to/oqSUSaBw#zostel=${propertyInfo.name}`,
      "_blank"
    );
  }, [propertyInfo.name]);

  return useMemo(
    () => (
      <section id="#book" ref={bookingElement}>
        <Container className="pt-4 pb-8 my-4 px-0 sm:px-8">
          <Flex items="start">
            <div className="w-full sm:w-2/3">
              <Flex col className="px-4 pb-4 sm:pl-1 sm:pr-2">
                <div className="flex-shrink-0 w-full sm:mr-4 sm:mb-4">
                  <h1 className="text-2xl font-black sm:text-4xl">
                    <span className="text-orange">{propertyInfo.name}</span>
                  </h1>

                  {(propertyInfo.type_code === "H" ||
                    propertyInfo.type_code === "P") &&
                    propertyInfo.operating_model === "F" && (
                      <div
                        style={{
                          backgroundColor: "#FFEFD1",
                          width: "fit-content",
                        }}
                        className="flex items-center gap-2 w-fit rounded-full py-1 px-2 mb-4 mt-1"
                      >
                        <Info className="w-4 h-4" />
                        <span className="text-sm text-primary">
                          Only for guests aged 18 & above
                        </span>
                      </div>
                    )}

                  {propertyInfo.tagline && (
                    <h2 className="font-medium text-text sm:text-xl">
                      {propertyInfo.tagline}
                    </h2>
                  )}
                  <CollapsibleText text={propertyInfo.description} showAsHTML />
                </div>
              </Flex>
              {window.innerWidth < 640 ? (
                <>
                  <Flex
                    items="start"
                    justify="between"
                    className="static flex-row px-4 py-2 shadow-sm sm:shadow-none sm:py-4 bg-gradient-to-r from-white to-gray-200 sm:flex-row"
                  >
                    <div className="w-full sm:text-left sm:w-1/2">
                      <h3 className="text-xl font-medium sm:text-3xl">
                        Book your stay
                      </h3>
                      <span className="text-sm font-medium text-text sm:text-base">
                        Select from a range of beautiful rooms
                      </span>
                    </div>
                    <Flex
                      items="center"
                      className="mt-2 mb-1 overflow-visible cursor-pointer"
                    >
                      {isDataLoading ? (
                        <Flex
                          items="center"
                          justify="center"
                          className="text-orange"
                        >
                          Loading
                          <span className="w-4 h-4 mx-2 border-b-2 rounded-full border-orange animate-spin" />
                        </Flex>
                      ) : (
                        <span
                          className="font-medium text-orange"
                          onClick={handleCurrencyDropDownClick}
                        >
                          {currency.slice(0, 3)}
                        </span>
                      )}

                      <Icon className="mx-1 text-xl font-medium text-orange">
                        keyboard_arrow_down
                      </Icon>
                      {isCurrencyDropdownVisible && (
                        <Flex
                          col
                          className="absolute left-0 z-20 px-4 py-3 mx-1 text-left bg-white border rounded-lg shadow-md"
                        >
                          <p className="my-2 text-base font-medium">
                            SELECTED CURRENCY
                          </p>
                          <p
                            className="px-2 py-1 mb-3 text-white rounded-lg bg-orange"
                            onClick={() => {
                              setIsCurrencyDropdownVisible(false);
                            }}
                          >
                            {
                              allCurrencies.find((k) => k.id === currency)
                                ?.value
                            }
                          </p>
                          <hr />
                          <p className="my-2 text-base font-medium">
                            OTHER CURRENCIES
                          </p>
                          <div className="h-32 px-2 overflow-scroll bg-gray-100 rounded-lg">
                            {allCurrencies.map((k) => (
                              <p
                                className="my-2"
                                onClick={() => {
                                  setCurrency(k.id);
                                }}
                              >
                                {k.value}
                              </p>
                            ))}
                          </div>
                        </Flex>
                      )}
                    </Flex>
                  </Flex>
                  {!hideDate && (
                    <Flex
                      items="center"
                      justify="between"
                      className="sticky top-0 z-10 flex-col py-2 shadow-sm sm:shadow-none bg-gradient-to-r from-white to-gray-200 sm:flex-row"
                    >
                      <div className="w-full px-4 sm:mt-0 sm:w-1/2">
                        <DateSelector
                          minDate={moment().add(bookingDelay, "days")}
                          maxDate={moment()
                            .add(bookingDelay, "days")
                            .add(bookableMonths, "months")}
                          startDate={moment(urlStartDate)}
                          setStartDate={(sd) => {
                            setUrlStartDate(sd.format("YYYY-MM-DD"));
                          }}
                          minNights={isMoveIn ? 7 : minNights}
                          endDate={moment(urlEndDate)}
                          setEndDate={(ed) => {
                            setUrlEndDate(ed.format("YYYY-MM-DD"));
                          }}
                        />
                      </div>
                      <div className="flex flex-row items-center gap-2 my-2">
                        <span className="text-sm font-medium text-secondary">
                          Guests
                        </span>
                        <div className="flex items-center justify-between bg-white rounded-xl border border-stroke-primary w-28 h-10">
                          <button
                            onClick={() => handleGuestCountChange(false)}
                            className="pl-4 h-full text-xl font-medium text-primary disabled:text-secondary focus:outline-none"
                            disabled={localGuestCount <= 1}
                          >
                            −
                          </button>
                          <span className="flex-1 text-center text-base">
                            {localGuestCount}
                          </span>
                          <button
                            onClick={() => handleGuestCountChange(true)}
                            className="pr-4 h-full text-xl font-medium text-primary focus:outline-none"
                          >
                            +
                          </button>
                        </div>
                      </div>
                    </Flex>
                  )}
                </>
              ) : (
                <Flex
                  items="center"
                  justify="between"
                  className="sticky top-0 z-10 flex-col py-2 border-b-2 border-r-2 border-gray-300 rounded-lg shadow-sm bg-gradient-to-r from-white to-gray-200 sm:shadow-md sm:py-4 sm:flex-row"
                >
                  <div className="w-full pl-4 text-center sm:text-left sm:w-1/2">
                    <h3 className="text-xl font-medium sm:text-3xl">
                      Book your stay
                    </h3>
                    <span className="text-sm font-medium text-text sm:text-base">
                      Select from a range of beautiful rooms
                    </span>
                  </div>

                  {!hideDate && (
                    <div className="w-full px-4 mt-4 sm:mt-0 sm:w-132">
                      <div className="flex flex-row items-end gap-4">
                        <div className="flex-1">
                          <DateSelector
                            minDate={moment().add(bookingDelay, "days")}
                            maxDate={moment()
                              .add(bookingDelay, "days")
                              .add(bookableMonths, "months")}
                            startDate={moment(urlStartDate)}
                            setStartDate={(sd) => {
                              setUrlStartDate(sd.format("YYYY-MM-DD"));
                            }}
                            endDate={moment(urlEndDate)}
                            setEndDate={(ed) => {
                              setUrlEndDate(ed.format("YYYY-MM-DD"));
                              const eventData = {
                                startDate:
                                  moment(urlStartDate).format("YYYY-MM-DD"),
                                endDate: ed.format("YYYY-MM-DD"),
                              };
                              gtag("event", "Select_Date", {
                                eventData,
                              });
                              trackZostelEvent("Select_Date", {
                                eventData,
                              });
                            }}
                            minNights={isMoveIn ? 7 : minNights}
                          />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-secondary mb-1">
                            Guests
                          </span>
                          <div className="flex items-center justify-between bg-white rounded-lg shadow-lg w-28 py-2">
                            <button
                              onClick={() => handleGuestCountChange(false)}
                              className="pl-4 h-full text-xl font-medium text-primary disabled:text-secondary focus:outline-none"
                              disabled={localGuestCount <= 1}
                            >
                              −
                            </button>
                            <span className="flex-1 text-center text-base">
                              {localGuestCount}
                            </span>
                            <button
                              onClick={() => handleGuestCountChange(true)}
                              className="pr-4 h-full text-xl font-medium text-primary disabled:text-secondary focus:outline-none"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </Flex>
              )}

              <Flex col className="px-4 pb-4 sm:pl-1 sm:pr-2">
                <RoomsList
                  roomCounts={roomCounts}
                  getRoomCountSetter={getRoomCountSetter}
                  rooms={rooms}
                  operatorCode={propertyCode}
                  checkInDate={urlStartDate || undefined}
                  checkOutDate={urlEndDate || undefined}
                  loadingAvailability={loadingAvailability}
                  setGalleryVisible={setGalleryVisible}
                  setGalleryData={setGalleryData}
                  selectedRoomId={selectedRoomId}
                />
                <div className="flex flex-col w-full p-4 mt-8 border border-gray-100 shadow-lg bg-gradient-to-r from-white to-gray-200">
                  <div className="flex flex-col">
                    <h2 className="text-2xl font-medium">Amenities</h2>
                  </div>

                  {propertyInfo.latitude && propertyInfo.longitude && (
                    <Container className="px-4 my-4" id="map">
                      <Flex className="flex-col sm:flex-row">
                        <div className="w-full sm:mr-4">
                          <Amenities
                            list={propertyInfo.amenities}
                            amenityClassname="pr-4 pt-2 w-1/2 sm:w-1/3"
                          />
                        </div>
                      </Flex>
                    </Container>
                  )}
                </div>
                <div className="flex flex-col w-full mt-8">
                  <div className="flex flex-col">
                    <h2 className="text-2xl font-medium">Contact Us</h2>
                  </div>
                  <Flex className="flex-col mt-6 gap-3 items-start sm:justify-start text-orange sm:flex-row">
                    <a
                      className="px-4 py-2 bg-white border rounded-lg border-accent"
                      href={`tel:${propertyInfo.phone}`}
                    >
                      <Icon className="mr-2">call</Icon> Call the Property
                    </a>

                    <a
                      href={`https://api.whatsapp.com/send?phone=919289229822&text=Hi%20Zobu,%20I%20want%20to%20talk%20about%20${propertyInfo.name}!`}
                      className="inline-flex items-center px-4 py-2  bg-white border rounded-lg border-accent text-orange"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Whatsapp className="w-4 h-4 mr-2 fill-current" />
                      WhatsApp Us
                    </a>
                  </Flex>
                </div>

                <div className="flex flex-col w-full mt-8">
                  <div className="flex flex-col">
                    <h2 className="text-2xl font-medium">Find Us</h2>
                  </div>

                  {propertyInfo.latitude && propertyInfo.longitude && (
                    <Container className="px-4 my-4" id="map">
                      <Flex className="flex-col sm:flex-row">
                        <div className="w-full sm:w-1/2 sm:mr-4">
                          {propertyInfo.address &&
                            propertyInfo.address !== "<p><br></p>" && (
                              <div className="flex flex-col p-2 space-y-1 border border-gray-200 rounded-lg">
                                <span className="font-medium">Address</span>
                                <div
                                  className="text-xs"
                                  dangerouslySetInnerHTML={{
                                    __html: propertyInfo.address || "",
                                  }}
                                />
                                <a
                                  className="items-center block px-4 py-2 mt-4 text-xs text-orange"
                                  href={`https://www.google.com/maps/dir/?api=1&destination=${propertyInfo.latitude},${propertyInfo.longitude}`}
                                >
                                  <Icon className="mr-2 text-xs">map</Icon>
                                  Tap To Open Maps
                                </a>
                              </div>
                            )}
                        </div>
                        <ZMap
                          centerLatitude={propertyInfo.latitude}
                          centerLongitude={propertyInfo.longitude}
                          className="z-0 w-full h-64 mt-4 rounded-lg sm:mt-0"
                          zoom={12}
                        />
                      </Flex>
                    </Container>
                  )}
                  <div className="grid w-full grid-cols-1 gap-4 mt-6 html-content">
                    {propertyInfo.directions &&
                      propertyInfo.directions !== "<p><br></p>" && (
                        <div ref={howToReachRef}>
                          <CollapsibleContent
                            expandOnHeaderClick
                            title="How to Reach"
                            html={propertyInfo.directions}
                            expanded={isHowToReachExpanded}
                          />
                        </div>
                      )}
                  </div>
                </div>

                {(propertyInfo.policy || propertyInfo.cancellation_policy) && (
                  <div className="flex flex-col w-full mt-8">
                    <div className="grid w-full grid-cols-1 gap-4 mt-6 html-content">
                      {propertyInfo.policy && (
                        <div ref={generalPolicyRef}>
                          <Accordion
                            title="General Policies"
                            className="border-b border-gray-200"
                            isDefaultExpanded={isGeneralPolicyExpanded}
                            children={
                              <div
                                className="whitespace-pre-line html-renderer-div pl-4"
                                dangerouslySetInnerHTML={{
                                  __html: propertyInfo.policy,
                                }}
                              />
                            }
                          />
                        </div>
                      )}
                      {propertyInfo.cancellation_policy && (
                        <div ref={cancellationPolicyRef}>
                          <Accordion
                            title="Cancellation Policy"
                            isDefaultExpanded={isCancellationPolicyExpanded}
                            children={
                              <>
                                {cancelInfo &&
                                  Array.isArray(cancelInfo) &&
                                  cancelInfo.length > 0 && (
                                    <>
                                      <CancellationPolicyGrid
                                        cancelInfo={cancelInfo}
                                      />
                                      <hr className="horizontal-divider my-2 sm:my-4" />
                                      <div
                                        className="whitespace-pre-line html-renderer-div pl-4"
                                        dangerouslySetInnerHTML={{
                                          __html:
                                            propertyInfo?.cancellation_policy ||
                                            "",
                                        }}
                                      />
                                    </>
                                  )}
                              </>
                            }
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </Flex>
            </div>
            <div className="sticky top-0 hidden sm:block sm:w-1/3 h-content">
              {showModal ? (
                !isMobile && (
                  <div className="p-6 rounded-2xl border border-accent shadow-lg ml-6 bg-white">
                    <h2 className="text-2xl leading-[32px] tracking-[0%] font-medium">
                      Traveling in a group?
                    </h2>
                    <div className="mt-4">
                      <img
                        src={`https://cdn.zo.xyz/gallery/media/images/86a5c1e5-5c0b-43b2-8665-81c505b3abfc_20250422084918.png`}
                        alt="Group Booking"
                        className="w-full h-auto"
                      />
                    </div>

                    <p className="mt-4 text-base leading-[24px] tracking-[0%] text-center">
                      Max {totalOccupancy} guests per booking allowed.{" "}
                      <span
                        onClick={() => setIsGroupPolicyExpanded((e) => !e)}
                        className="underline cursor-pointer"
                      >
                        Why?
                      </span>
                    </p>

                    {isGroupPolicyExpanded && (
                      <p className="mt-4 text-sm leading-[20px] tracking-[0%] text-left text-secondary bg-light-grey pr-8 -mx-6 pl-6 py-2">
                        At Zostel, we celebrate solo trips & chance connections.
                        One's a seeker, two’s company, three’s a memory.{" "}
                        {totalOccupancy}? That’s the start of a reality show.
                        <br />
                        To keep the magic alive for all, groups of{" "}
                        {totalOccupancy} get curated differently. Drop your
                        details, & we’ll help out.
                      </p>
                    )}

                    <p
                      className={`text-sm leading-[20px] tracking-[0%] text-center text-secondary ${
                        isGroupPolicyExpanded ? "mt-4" : "mt-1"
                      }`}
                    >
                      For larger groups, please share details.
                    </p>

                    <Button
                      className="mt-4 w-full"
                      onClick={handleShareGroupBookingDetail}
                    >
                      Share Details
                    </Button>
                  </div>
                )
              ) : (
                <BookingSummary
                  scrollToBooking={scrollToBooking}
                  scrollToOtherStays={scrollToOtherStays}
                  propertyInfo={propertyInfo}
                  bookingDetails={bookingDetails}
                  rooms={rooms}
                  getRoomCountSetter={getRoomCountSetter}
                  propertyName={propertyInfo.destination?.name}
                  showSimilarProperty={
                    moreProperties && moreProperties.length > 0
                  }
                  guestCount={localGuestCount}
                />
              )}
            </div>
          </Flex>
        </Container>

        {isMobile && (
          <BottomSheet
            style={{
              zIndex: 1000,
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
            }}
            open={showModal}
            onDismiss={() => setShowModal(false)}
          >
            <div className="p-6 bg-white">
              <h2 className="text-2xl leading-[32px] tracking-[0%] font-medium">
                Traveling in a group?
              </h2>
              <div className="mt-4">
                <img
                  src={`https://cdn.zo.xyz/gallery/media/images/86a5c1e5-5c0b-43b2-8665-81c505b3abfc_20250422084918.png`}
                  alt="Group Booking"
                  className="w-full h-auto"
                />
              </div>

              <p className="mt-4 text-base leading-[24px] tracking-[0%] text-center">
                Max {totalOccupancy} guests per booking allowed.{" "}
                <span
                  onClick={() => setIsGroupPolicyExpanded((e) => !e)}
                  className="underline cursor-pointer"
                >
                  Why?
                </span>
              </p>

              {isGroupPolicyExpanded && (
                <p className="mt-4 text-sm leading-[20px] tracking-[0%] text-left text-secondary bg-light-grey pr-8 -mx-6 pl-6 py-2">
                  At Zostel, we celebrate solo trips & chance connections. One's
                  a seeker, two’s company, three’s a memory. {totalOccupancy}?
                  That’s the start of a reality show.
                  <br />
                  To keep the magic alive for all, groups of {
                    totalOccupancy
                  }{" "}
                  get curated differently. Drop your details, & we’ll help out.
                </p>
              )}

              <p
                className={`text-sm leading-[20px] tracking-[0%] text-center text-secondary ${
                  isGroupPolicyExpanded ? "mt-4" : "mt-1"
                }`}
              >
                For larger groups, please share details.
              </p>

              <Button
                className="mt-4 w-full"
                onClick={handleShareGroupBookingDetail}
              >
                Share Details
              </Button>
            </div>
          </BottomSheet>
        )}
      </section>
    ),
    [
      isHowToReachExpanded,
      isGeneralPolicyExpanded,
      isCancellationPolicyExpanded,
      showModal,
      isDataLoading,
      currency,
      isCurrencyDropdownVisible,
      allCurrencies,
      hideDate,
      bookingDelay,
      bookableMonths,
      urlStartDate,
      isMoveIn,
      minNights,
      urlEndDate,
      roomCounts,
      getRoomCountSetter,
      rooms,
      propertyCode,
      loadingAvailability,
      bookingDetails,
      setCurrency,
      setUrlStartDate,
      setUrlEndDate,
      setGalleryData,
      setGalleryVisible,
      propertyInfo,
      moreProperties,
      scrollToOtherStays,
      cancelInfo,
      selectedRoomId,
      localGuestCount,
      handleGuestCountChange,
      isGroupPolicyExpanded,
      isMobile,
      handleShareGroupBookingDetail,
      totalOccupancy,
    ]
  );
};

export default PropertyBooker;
